import { FC, useEffect, useState } from "react";
import { Button, Image, Tag, Typography } from "antd";
import Table from "antd/es/table";
import { ColumnsType } from "antd/es/table";
import { useGetPayoutList, useExportPayoutItems } from "hooks/useFactory";
import { PayoutBatch } from "services/interface";
import { formatDateAndTime } from "utils/convertData";
import { numberFormatePrice } from "utils/formateNumber";
import styled from "@emotion/styled";
import ModalPayoutDetail from "./ModalPayoutDetail";
import { getScrollX } from "./TableList";
import ReceiptFiles, { getReceiptUrls } from "./ReceiptFiles";

const TableCompletedSummary: FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [tableData, setTableData] = useState<PayoutBatch[]>([]);
  const [selectedPayoutId, setSelectedPayoutId] = useState<string | null>(null);
  const { mutate: exportItems, isPending: isExporting } = useExportPayoutItems();

  const { data } = useGetPayoutList({
    payout_status: "paid",
    receipt_uploaded: true,
    page_number: page,
    row_per_page: pageSize,
  });

  useEffect(() => {
    if (data) setTableData(data.data.data);
  }, [data]);

  const columns: ColumnsType<PayoutBatch> = [
    {
      title: "หมายเลขรายการ",
      dataIndex: "payout_code",
      width: 150,
      render: (_text, record) => (
        <Typography.Text>{record.payout_code}</Typography.Text>
      ),
    },
    {
      title: "จำนวนรายการ",
      dataIndex: "item_count",
      width: 100,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>{record.item_count}</Typography.Text>
      ),
    },
    {
      title: "ยอดรวม (บาท)",
      dataIndex: "total_amount",
      width: 120,
      align: "right",
      render: (_text, record) => (
        <Typography.Text strong style={{ color: "#389e0d" }}>
          {numberFormatePrice(record.total_amount)}
        </Typography.Text>
      ),
    },
    {
      title: "สถานะ",
      dataIndex: "payout_status",
      width: 100,
      align: "center",
      render: () => <Tag color="green">เสร็จสิ้น</Tag>,
    },
    {
      title: "วันที่ทำรายการ",
      dataIndex: "create_date",
      width: 120,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>
          {formatDateAndTime(record.create_date)}
        </Typography.Text>
      ),
    },
    {
      title: "วันที่โอนเงิน",
      dataIndex: "paid_at",
      width: 120,
      align: "center",
      render: (_text, record) => (
        <Typography.Text>
          {record.paid_at ? formatDateAndTime(record.paid_at) : "-"}
        </Typography.Text>
      ),
    },
    {
      title: "บัญชีรับเงิน",
      dataIndex: "bank_name",
      render: (_text, record) => (
        <div style={{ whiteSpace: "normal" }}>
          <Typography.Text>{record.bank_name ?? "-"}</Typography.Text>
          <br />
          <Typography.Text>
            {record.recipient_account ?? "-"} · {record.receiver_name ?? "-"}
          </Typography.Text>
        </div>
      ),
    },
    // {
    //   title: "สลิป",
    //   dataIndex: "payment_evidence_url",
    //   width: 160,
    //   align: "center",
    //   render: (_text, record) =>
    //     record.payment_evidence_url ? (
    //       <Image
    //         src={record.payment_evidence_url}
    //         width={48}
    //         height={48}
    //         style={{ objectFit: "contain", borderRadius: 4 }}
    //       />
    //     ) : (
    //       <Typography.Text>-</Typography.Text>
    //     ),
    // },
    // {
    //   title: "ใบเสร็จรับเงิน",
    //   dataIndex: "receipt_url",
    //   width: 130,
    //   align: "center",
    //   render: (_text, record) => {
    //     const urls = getReceiptUrls(record);
    //     return urls.length ? <ReceiptFiles urls={urls} size={48} /> : <Typography.Text>-</Typography.Text>;
    //   },
    // },
    {
      title: "จัดการ",
      key: "action",
      width: 150,
      align: "center",
      fixed: "right",
      render: (_text, record) => (
        <div style={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <Button type="default" size="small" onClick={() => setSelectedPayoutId(record.id)} style={{ fontSize: 10 }}>
            ดูรายละเอียด
          </Button>
          <Button type="default" size="small" onClick={() => exportItems(record.id)} loading={isExporting} style={{ fontSize: 10 }}>
            Excel
          </Button>
        </div>
      ),
    },
  ];

  const total = data?.data.pagination.total ?? 0;

  return (
    <CardWrapperTable>
      <TableTitle>รายการเสร็จสิ้น ({total})</TableTitle>
      <Table
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={tableData}
        scroll={{ y: 600, x: getScrollX(columns) }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `ทั้งหมด ${t} รายการ`,
          onChange: (p, size) => {
            setPage(p);
            setPageSize(size);
          },
        }}
      />
      <ModalPayoutDetail
        payoutId={selectedPayoutId}
        bankFactoryId={tableData.find((item) => item.id === selectedPayoutId)?.bank_factory_id ?? null}
        onClose={() => setSelectedPayoutId(null)}
      />
    </CardWrapperTable>
  );
};

export default TableCompletedSummary;

const CardWrapperTable = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: auto;
  background-color: #ffffff;
  padding: 5px;
`;

const TableTitle = styled.p`
  font-size: 16px;
  font-weight: bold;
  padding-left: 10px;
  padding-top: 10px;
`;
